import React, { useEffect, useState } from "react";
import { Baby, Plus, CheckCircle2, XCircle, ShieldCheck, Clock, Gamepad2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import MonetizationApprovals from "@/components/MonetizationApprovals";
import PurchaseApprovals from "@/components/PurchaseApprovals";
import { toast } from "sonner";

export default function ParentDashboard() {
  const [dash, setDash] = useState(null);
  const [children, setChildren] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", password: "", age: 8 });

  const load = () => Promise.all([
    api.get("/parent/dashboard"), api.get("/parent/children"), api.get("/parent/approvals"),
  ]).then(([d, c, a]) => { setDash(d.data); setChildren(c.data); setApprovals(a.data); });
  useEffect(() => { load(); }, []);
  if (!dash) return <Spinner />;

  const createChild = async () => {
    try {
      const { data } = await api.post("/parent/children", { ...form, age: Number(form.age) });
      toast.success(`Account created! Login username: ${data.login_username}`);
      setOpen(false); setForm({ name: "", username: "", password: "", age: 8 });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const decide = async (id, action) => {
    const { data } = await api.post(`/parent/approvals/${id}/${action}`);
    toast.success(data.message);
    load();
  };

  const toggle = async (childId, key, value) => {
    await api.post(`/parent/children/${childId}/settings`, { [key]: value });
    setChildren((cs) => cs.map((c) => c.id === childId ? { ...c, settings: { ...c.settings, [key]: value } } : c));
  };

  return (
    <div className="p-5 lg:p-8 max-w-5xl mx-auto">
      <PageHeader title="Parent Dashboard" icon={Baby} subtitle="Manage children, approvals and safety controls."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="add-child-btn"><Plus className="w-4 h-4" /> Add Child</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Create a child account</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Child's name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="child-name" className="mt-1.5" /></div>
                <div><Label>Login username</Label><Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="e.g. kidstar" data-testid="child-username" className="mt-1.5" /></div>
                <div><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="child-password" className="mt-1.5" /></div>
                <div><Label>Age</Label><Input type="number" min={4} max={17} value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} data-testid="child-age" className="mt-1.5" /></div>
              </div>
              <DialogFooter><Button onClick={createChild} data-testid="create-child-confirm" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">Create account</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Children" value={dash.children_count} icon={Baby} delay={1} />
        <StatCard label="Pending approvals" value={dash.pending_approvals} icon={Clock} accent="#f59e0b" delay={2} />
        <StatCard label="Games created" value={dash.games_created} icon={Gamepad2} accent="#10b981" delay={3} />
        <StatCard label="Published" value={dash.games_published} icon={ShieldCheck} accent="#a855f7" delay={4} />
      </div>

      {/* Approval queue */}
      <PurchaseApprovals />
      <MonetizationApprovals endpoint="/parent/monetization/pending" title="Premium packs awaiting your approval" />
      <h2 className="font-display text-xl font-bold mb-3">Games awaiting your approval</h2>
      {approvals.length === 0 ? (
        <EmptyState emoji="✅" title="All caught up!" text="No games waiting for approval right now." />
      ) : (
        <div className="space-y-3 mb-8">
          {approvals.map((r) => (
            <div key={r.id} className="rounded-2xl border border-amber-500/30 bg-[hsl(var(--card))] p-4 flex items-center gap-4" data-testid={`approval-${r.id}`}>
              <div className="w-14 h-14 rounded-xl grid place-items-center text-3xl" style={{ background: `linear-gradient(135deg, ${r.game?.config?.world?.bgColor || "#0b1026"}, ${r.game?.config?.world?.accent || "#1f6feb"})` }}>{r.cover_emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{r.title}</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))]">by {r.child_name} · 🛡️ Safe content</div>
              </div>
              <Button size="sm" onClick={() => decide(r.id, "approve")} data-testid={`approve-${r.id}`} className="rounded-full bg-emerald-500 hover:bg-emerald-600"><CheckCircle2 className="w-4 h-4" /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")} data-testid={`reject-${r.id}`} className="rounded-full"><XCircle className="w-4 h-4" /> Reject</Button>
            </div>
          ))}
        </div>
      )}

      {/* Children & controls */}
      <h2 className="font-display text-xl font-bold mb-3">Children & controls</h2>
      {children.length === 0 ? (
        <EmptyState emoji="👶" title="No children yet" text="Add a child account to get started." />
      ) : (
        <div className="space-y-4">
          {children.map((c) => (
            <div key={c.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full grid place-items-center text-2xl bg-gradient-to-br from-[#1f6feb] to-[#3b9eff]">{c.avatar_emoji}</div>
                <div className="flex-1"><div className="font-display font-bold text-lg">{c.name}</div><div className="text-sm text-[hsl(var(--muted-foreground))]">@{c.username} · age {c.age} · {c.games_created} games</div></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <ToggleRow label="Allow public publishing" checked={c.settings?.allow_public_publish} onChange={(v) => toggle(c.id, "allow_public_publish", v)} testid={`toggle-publish-${c.id}`} />
                <ToggleRow label="Allow AI image generation" checked={c.settings?.allow_ai_images} onChange={(v) => toggle(c.id, "allow_ai_images", v)} testid={`toggle-ai-${c.id}`} />
                <ToggleRow label="Allow comments / social" checked={c.settings?.allow_social} onChange={(v) => toggle(c.id, "allow_social", v)} testid={`toggle-social-${c.id}`} />
                <ToggleRow label="School-only mode" checked={c.settings?.school_only} onChange={(v) => toggle(c.id, "school_only", v)} testid={`toggle-school-${c.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[hsl(var(--secondary))]/40">
    <span className="text-sm font-medium">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);
